// projectWizard/useProjectForm.js
//
// A small controlled-form engine. This exists instead of react-hook-form
// because RHF is not a dependency of this project and the npm registry is
// unreachable from the build environment (403) — adding it is not an option.
// The behaviour RHF would have provided is reproduced here deliberately:
// per-field state, touched tracking, per-step validation, dirty detection,
// and a stable setter identity so memoised step components do not re-render
// when unrelated fields change.
//
// Re-render discipline: `setField` is wrapped in useCallback with an empty
// dependency list and updates state functionally, so its identity never
// changes across the life of the form. Step components can therefore be
// wrapped in React.memo and will only re-render when their own values move.

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  DEFAULT_PROJECT_FORM, validateStep, validateAll, completionPercent, WIZARD_STEPS,
} from './projectFormModel';

const DRAFT_KEY_PREFIX = 'ngb_project_draft_';
const AUTOSAVE_DELAY_MS = 2500;

const readDraft = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const clearProjectDraft = (draftId) => {
  try {
    localStorage.removeItem(DRAFT_KEY_PREFIX + (draftId || 'new'));
  } catch (e) {
    /* storage unavailable — nothing to clear */
  }
};

/**
 * @param {object}  options
 * @param {object}  options.initialValues  hydrated project (edit) or undefined (create)
 * @param {string}  options.draftId        'new' for create, the project id for edit
 * @param {boolean} options.autosave       persist a local draft while typing
 */
export const useProjectForm = ({ initialValues, draftId = 'new', autosave = true } = {}) => {
  const draftKey = DRAFT_KEY_PREFIX + draftId;

  const [values, setValues] = useState(() => ({ ...DEFAULT_PROJECT_FORM, ...(initialValues || {}) }));
  const [touched, setTouched] = useState({});
  const [dirty, setDirty] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // The pristine snapshot to diff against. Kept in a ref so changing it does
  // not itself trigger a render.
  const baselineRef = useRef({ ...DEFAULT_PROJECT_FORM, ...(initialValues || {}) });
  const autosaveTimer = useRef(null);

  /**
   * Replace the whole form — used when an edit target's data arrives.
   * This is the piece the old implementation lacked entirely: state seeded
   * with useState() alone never updates when the source data loads later,
   * which is exactly why an edit form would have rendered blank.
   */
  const hydrate = useCallback((next) => {
    const merged = { ...DEFAULT_PROJECT_FORM, ...(next || {}) };
    baselineRef.current = merged;
    setValues(merged);
    setTouched({});
    setDirty(false);
  }, []);

  const setField = useCallback((field, value) => {
    setValues((prev) => {
      if (prev[field] === value) return prev; // no-op writes must not re-render
      return { ...prev, [field]: value };
    });
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    setDirty(true);
  }, []);

  const setMany = useCallback((patch) => {
    setValues((prev) => ({ ...prev, ...(patch || {}) }));
    setDirty(true);
  }, []);

  const reset = useCallback(() => {
    setValues({ ...baselineRef.current });
    setTouched({});
    setDirty(false);
  }, []);

  const markSaved = useCallback((savedValues) => {
    const next = savedValues ? { ...DEFAULT_PROJECT_FORM, ...savedValues } : null;
    if (next) baselineRef.current = next;
    else baselineRef.current = { ...values };
    setDirty(false);
  }, [values]);

  // ── Autosave (local draft only) ───────────────────────────────────────────
  // There is no draft endpoint on the backend and adding one is out of scope,
  // so the draft lives in localStorage. It is a crash/refresh safety net, not
  // a server-side save — the UI labels it accordingly.
  useEffect(() => {
    if (!autosave || !dirty) return undefined;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(values));
        setDraftSavedAt(new Date());
      } catch (e) {
        /* quota or private mode — autosave is best-effort by design */
      }
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [values, dirty, autosave, draftKey]);

  const restoreDraft = useCallback(() => {
    const draft = readDraft(draftKey);
    if (draft) {
      setValues((prev) => ({ ...prev, ...draft }));
      setDirty(true);
      return true;
    }
    return false;
  }, [draftKey]);

  const hasDraft = useCallback(() => readDraft(draftKey) !== null, [draftKey]);

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const stepErrors = useMemo(() => validateAll(values), [values]);
  const completion = useMemo(() => completionPercent(values), [values]);

  const errorsForStep = useCallback((stepId) => validateStep(stepId, values), [values]);

  const isStepValid = useCallback(
    (stepId) => Object.keys(validateStep(stepId, values)).length === 0,
    [values]
  );

  const firstInvalidStep = useMemo(() => {
    const found = WIZARD_STEPS.find((s) => stepErrors[s.id]);
    return found ? found.id : null;
  }, [stepErrors]);

  return {
    values, touched, dirty, completion, stepErrors, firstInvalidStep, draftSavedAt,
    setField, setMany, hydrate, reset, markSaved,
    errorsForStep, isStepValid, restoreDraft, hasDraft,
  };
};

export default useProjectForm;
